import React, { KeyboardEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { UserSettingLayoutContainer } from "../UserSettingLayoutContainer";
import { Hotkeys, useGlobalStore, usePreferencesStore, defaultHotkeys } from "@obsbot/remo-stores";
import { useTranslate } from "@obsbot/remo-i18n";
import { Input } from "antd";
import { RuntimeContext } from "../../components/StoreProvider";
import { withMessage } from "remo-components";
import "./index.less";

interface HotkeyItem {
    key: keyof Hotkeys;
    label: string;
    value: string;
}

enum HotkeyType {
    Default,
    Gameoverlay,
}

const ignoreKey = ["Process"];

const HotkeySettingPage = withMessage(({ message }): React.ReactElement => {
    const globalStore = useGlobalStore();
    const preferenceStore = usePreferencesStore(globalStore.userInfo?.user_id ?? "")?.(
        state => state,
    );
    const t = useTranslate();
    const runtime = useContext(RuntimeContext);
    const ignoreRestore = useRef(false);
    const [hotkeys, setHotkeys] = useState<Hotkeys>(preferenceStore?.hotkeys ?? defaultHotkeys);
    useEffect(() => {
        if (preferenceStore?.hotkeys) {
            const newHotkeys = preferenceStore.hotkeys;
            const isEqual = Object.keys(newHotkeys).every(key => {
                return (
                    JSON.stringify(newHotkeys[key as keyof Hotkeys]) ===
                    JSON.stringify(hotkeys[key as keyof Hotkeys])
                );
            });
            if (!isEqual && !ignoreRestore.current) {
                setHotkeys({ ...newHotkeys });
                ignoreRestore.current = true;
            }
        }
    }, [preferenceStore?.hotkeys, hotkeys]);
    // 默认快捷键
    const defaultHotKeys = useMemo<HotkeyItem[]>(() => {
        const defaultHotkeyKeys: Array<keyof Hotkeys> = ["CopyText", "PasteText", "CreateChannel"];
        return defaultHotkeyKeys.map(hotkeyKey => ({
            key: hotkeyKey,
            label: t(`settings.hotkey-setting.${hotkeyKey}`),
            value: hotkeys[hotkeyKey as keyof Hotkeys].key,
            keyCodeValue: hotkeys[hotkeyKey as keyof Hotkeys].value,
        }));
    }, [hotkeys, t]);
    const gameoverlayHotKeys = useMemo<HotkeyItem[]>(() => {
        const defaultHotkeyKeys: Array<keyof Hotkeys> = [
            "Switch2Audio",
            "Switch2Video",
            "SwitchMic",
            "CallEmoticons",
        ];
        return defaultHotkeyKeys.map(hotkeyKey => ({
            key: hotkeyKey,
            label: t(`settings.hotkey-setting.${hotkeyKey}`),
            value: hotkeys[hotkeyKey as keyof Hotkeys].key,
            keyCodeValue: hotkeys[hotkeyKey as keyof Hotkeys].value,
        }));
    }, [hotkeys, t]);
    useEffect(() => {
        const preventPaste = (e: any): void => {
            if (e.key === "v" + e.ctrlKey) {
                e.preventDefault();
            }
        };
        window.addEventListener("keydown", preventPaste);
        return () => window.removeEventListener("keydown", preventPaste);
    }, []);
    const metaKey = runtime?.isMac ? "Command" : "Windows";
    const identifierKey = ["Ctrl", "Control", "Shift", "Alt", metaKey, "Meta"];
    // 设置快捷键的事件
    const handleHotkeyKeydown = (e: KeyboardEvent, type: HotkeyType): void => {
        e.preventDefault();
        if (type === HotkeyType.Default) {
            const settingHotkey = handleSettingHotkey(e, metaKey, identifierKey);
            if (settingHotkey) {
                const { name, hotkey, hotkeyValues } = settingHotkey;
                if (Object.values(hotkeys).some(primeKey => primeKey.key === hotkey)) {
                    return void message.warning(t("settings.hotkey-setting.hotkeySame"));
                }
                ignoreRestore.current = true;
                setHotkeys(hotkeys => ({
                    ...hotkeys,
                    [name]: {
                        key: hotkey,
                        value: hotkeyValues,
                    },
                }));
            }
        } else {
            const settingHotkey = handleSettingHotkey(e, metaKey, identifierKey);
            if (settingHotkey) {
                const { name, hotkey, hotkeyValues } = settingHotkey;
                if (Object.values(hotkeys).some(primeKey => primeKey.key === hotkey)) {
                    return void message.warning(t("settings.hotkey-setting.hotkeySame"));
                }
                ignoreRestore.current = true;
                setHotkeys(hotkeys => ({
                    ...hotkeys,
                    [name]: {
                        key: hotkey,
                        value: hotkeyValues,
                    },
                }));
            }
        }
    };
    const handleHotkeyInputBlur = (): void => {
        preferenceStore?.updateHotkeys(hotkeys);
    };
    return (
        <UserSettingLayoutContainer>
            <div className="hotkeys-setting-container">
                <div className="hotkeys-setting-default">
                    <div className="setting-page-title">
                        <div className="title">{t("settings.hotkey-setting.defaultHotkey")}</div>
                    </div>
                    <div className="setting-page-content">
                        {defaultHotKeys.map(hotkey => (
                            <div key={hotkey.label} className="setting-page-content-item">
                                <div className="setting-show-item-between">
                                    <div className="setting-page-label">{hotkey.label}</div>
                                    <div className="setting-show-item-input">
                                        <Input
                                            className={"hotkey-setting-input"}
                                            name={hotkey.key}
                                            readOnly={true}
                                            value={hotkey.value}
                                            onBlur={handleHotkeyInputBlur}
                                            onKeyDown={e =>
                                                handleHotkeyKeydown(e, HotkeyType.Default)
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                {(window as any).electron && (
                    <div className="hotkeys-setting-gameoverlay">
                        <div className="setting-page-title">
                            <div className="title">{t("layout.overlay")}</div>
                        </div>
                        <div className="setting-page-content">
                            {gameoverlayHotKeys.map(hotkey => (
                                <div key={hotkey.label} className="setting-page-content-item">
                                    <div className="setting-show-item-between">
                                        <div className="setting-page-label">{hotkey.label}</div>
                                        <div className="setting-show-item-input">
                                            <Input
                                                className={`hotkey-setting-input ${"hotkey-setting-input-edit"}`}
                                                name={hotkey.key}
                                                readOnly={true}
                                                value={hotkey.value}
                                                onBlur={handleHotkeyInputBlur}
                                                onKeyDown={e =>
                                                    handleHotkeyKeydown(e, HotkeyType.Gameoverlay)
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </UserSettingLayoutContainer>
    );
});

function handleSettingHotkey(
    e: KeyboardEvent,
    metaKey: string,
    identifierKey: string[],
): { name: string; hotkey: string; hotkeyValues: number[] } | undefined {
    const isAltOn = e.altKey;
    const isCtrlOn = e.ctrlKey;
    const isShiftOn = e.shiftKey;
    const isMetaOn = e.metaKey;
    const key = e.key;
    const keyCode = e.keyCode;
    const name = (e.target as any).name;
    const hotkeyValues: number[] = [];
    const hotkeyOrder = [isCtrlOn, isShiftOn, isAltOn, isMetaOn, key];
    const hotkeyOrderMap = ["Ctrl", "Shift", "Alt", metaKey, "Key"];
    const hotkeyValuesMap = [162, 160, 164, 91];
    const hotkeyReduce = hotkeyOrder.reduce((prev, key, idx) => {
        const keyMap = idx === hotkeyOrder.length - 1 ? (key as string) : hotkeyOrderMap[idx];
        if (key) {
            if (ignoreKey.includes(keyMap)) {
                return prev;
            }
            if (prev === "") {
                return (prev = keyMap);
            }
            return (prev = prev + "+" + keyMap);
        }
        return prev;
    }, "");
    let hotkey: string = hotkeyReduce as string;
    const hotkeySplit = hotkey.split("+");
    // 如果只有identifierKey就不更新
    if (
        identifierKey.includes(hotkey as string) ||
        hotkeySplit.every(item => identifierKey.includes(item)) ||
        hotkeySplit.length === 1
    ) {
        return;
    }
    hotkeySplit[hotkeySplit.length - 1] = hotkeySplit[hotkeySplit.length - 1].toUpperCase();
    hotkeySplit.forEach(item => {
        const findIndex = hotkeyOrderMap.findIndex(order => order === item);
        if (findIndex === -1) {
            hotkeyValues.push(keyCode);
        } else {
            hotkeyValues.push(hotkeyValuesMap[findIndex]);
        }
    });
    hotkey = hotkeySplit.join("+");
    return { name, hotkey, hotkeyValues };
}

export default HotkeySettingPage;
